// Secure AI integration via backend proxy
import ApiService from './api';

// Optional default model hint if explicitly configured in environment
const DEFAULT_MODEL_HINT = process.env.REACT_APP_CURSOR_MODEL || process.env.REACT_APP_OPENAI_MODEL || '';

export async function generateAITestArtifacts(task, prompt, opts = {}) {
  // Call backend proxy: POST /api/ai/chat
  // Default to bundle/auto mode so prompts can create multiple artifacts.
  // Preserve explicit tasks used elsewhere.
  const passthroughTasks = new Set(['test-data-sets', 'failure-analysis']);
  const effectiveTask = passthroughTasks.has(task) ? task : 'auto';

  const payload = { task: effectiveTask, prompt };
  const hint = opts?.modelHint || DEFAULT_MODEL_HINT;
  if (hint) payload.modelHint = hint;

  const data = await ApiService.makeRequest('/ai/chat', { method: 'POST', body: JSON.stringify(payload) });
  return data; // already JSON from server
}

/** Multi-turn AI Companion chat (free-form Q&A + Testera tools). */
export async function chatAICompanion(messages, opts = {}) {
  const history = Array.isArray(messages) ? messages : [];
  const latest = [...history].reverse().find((m) => m?.role === 'user')?.content || '';
  const payload = {
    task: opts.task || 'companion',
    prompt: String(latest || opts.prompt || '').trim(),
    messages: history.map((m) => ({
      role: m.role === 'assistant' ? 'assistant' : 'user',
      content: String(m.content || ''),
    })),
  };
  const hint = opts?.modelHint || DEFAULT_MODEL_HINT;
  if (hint) payload.modelHint = hint;
  if (opts.onboarding) payload.onboarding = true;
  if (!payload.prompt) throw new Error('Message required');
  return ApiService.makeRequest('/ai/chat', { method: 'POST', body: JSON.stringify(payload) });
}

/** Admin Portal companion (superadmin tools — tenants, visitors, audit, ops). */
export async function chatAdminCompanion(messages, opts = {}) {
  return chatAICompanion(messages, { ...opts, task: 'admin-companion' });
}

export async function aiInferTestCasesFromTable(rows, opts = {}) {
  if (!Array.isArray(rows) || rows.length === 0) throw new Error('No rows provided');
  const payload = { rows, maxRows: opts.maxRows || 30 };
  const hint = opts?.modelHint || DEFAULT_MODEL_HINT;
  if (hint) payload.modelHint = hint;

  const data = await ApiService.makeRequest('/ai/table', { method: 'POST', body: JSON.stringify(payload) });
  if (!data.testCases) throw new Error('AI response missing testCases');
  return data;
}

export async function analyzeTestFailure(context, opts = {}) {
  const task = 'failure-analysis';
  const prompt = JSON.stringify({ context }, null, 2);
  const payload = { task, prompt };
  const hint = opts?.modelHint || DEFAULT_MODEL_HINT;
  if (hint) payload.modelHint = hint;

  const data = await ApiService.makeRequest('/ai/chat', { method: 'POST', body: JSON.stringify(payload) });
  return data;
}

export async function generateTestDataSets(testCase, opts = {}) {
  const task = 'test-data-sets';
  const prompt = JSON.stringify({ testCase }, null, 2);
  const payload = { task, prompt };
  const hint = opts?.modelHint || DEFAULT_MODEL_HINT;
  if (hint) payload.modelHint = hint;

  const data = await ApiService.makeRequest('/ai/chat', { method: 'POST', body: JSON.stringify(payload) });
  return data;
}

// Cached: this used to fire a network request on every call, and callers were
// invoking it during render — flooding the browser with concurrent requests
// until fetches failed with ERR_INSUFFICIENT_RESOURCES.
const AI_AVAILABILITY_TTL_MS = 5 * 60 * 1000;
let aiAvailability = { ts: 0, value: null, pending: null };

export async function isAIAvailable() {
  const now = Date.now();
  if (aiAvailability.value !== null && now - aiAvailability.ts < AI_AVAILABILITY_TTL_MS) {
    return aiAvailability.value;
  }
  if (aiAvailability.pending) return aiAvailability.pending;
  aiAvailability.pending = ApiService.makeRequest('/ai/availability')
    .then((res) => {
      aiAvailability = { ts: Date.now(), value: !!res.available, pending: null };
      return aiAvailability.value;
    })
    .catch(() => {
      aiAvailability = { ts: Date.now(), value: false, pending: null };
      return false;
    });
  return aiAvailability.pending;
}
